import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GetCampaignInfo,
  UpdateCampaignDetails,
  CreateCampaignDraft,
} from "../../services/campaign";
import EditCampaignStep from "./tabs/edit-campaign";
import { keyframes, styled } from "@mui/material/styles";
import { Tabs, Tab, Box, CircularProgress } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import LoopIcon from "@mui/icons-material/Loop";
import { Check } from "react-feather";
import EditLoopStep from "./tabs/edit-loop";
import PopSnackbar from "../../components/snackbar";
import AlertModal from "../../components/modal";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledTabs = styled(Tabs)(({ theme }) => ({
  position: "relative",
  width: "100%",
  background: "linear-gradient(135deg, #fafafa, #fff)",
  borderRadius: "8px 8px 0 0",
  boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
  overflow: "hidden",
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    fontSize: "1rem",
    padding: theme.spacing(2, 1),
    fontFamily: "SeagoeUI, sans-serif",
    color: "rgba(0, 0, 0, 0.65)",
    opacity: 0.85,
    transition: "all 0.3s ease",
    position: "relative",
    width: "50%",
    borderRight: "1px solid rgba(0, 0, 0, 0.05)",
    "&:last-of-type": {
      borderRight: "none",
    },
    "& .MuiTab-iconWrapper": {
      marginRight: theme.spacing(1),
      fontSize: 22,
      transition: "transform 0.3s ease",
    },
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.03)",
      color: "#000",
      opacity: 1,
      "& .MuiTab-iconWrapper": {
        transform: "scale(1.1)",
      },
    },
    "&.Mui-selected": {
      color: "#000",
      opacity: 1,
      fontFamily: "SeagoeUIBold, sans-serif",
    },
  })
);

const StepIndicator = styled("span")(({ active, completed }) => ({
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: "12px",
  fontSize: "0.875rem",
  backgroundColor: completed ? "#000" : active ? "rgba(0, 0, 0, 0.1)" : "#fff",
  color: completed ? "#fff" : active ? "#000" : "rgba(0, 0, 0, 0.75)",
  border: `2px solid ${
    completed
      ? "#000"
      : active
      ? "rgba(97, 97, 97, 0.4)"
      : "rgba(97, 97, 97, 0.2)"
  }`,
  transition: "all 0.3s ease",
  transform: active ? "scale(1.1)" : "scale(1)",
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  animation: `${slideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
  "& > *": {
    animation: `${slideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
  },
}));

const ProgressArrow = styled("div")(({ activeTab }) => ({
  position: "absolute",
  left: activeTab === 0 ? "25%" : "75%",
  transform: "translateX(-50%)",
  width: 0,
  height: 0,
  borderLeft: "10px solid transparent",
  borderRight: "10px solid transparent",
  borderTop: "10px solid #ff4757",
  transition: "left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    background: "#ff4757",
    animation: "particles 1.5s infinite",
    opacity: 0,
  },
  "&::before": {
    animationDelay: "0.2s",
    left: "-15px",
  },
  "&::after": {
    animationDelay: "0.4s",
    right: "-15px",
  },
  "@keyframes particles": {
    "0%": {
      transform: "translateY(0) scale(1)",
      opacity: 0,
    },
    "50%": {
      transform: "translateY(-20px) scale(0.5)",
      opacity: 0.8,
    },
    "100%": {
      transform: "translateY(-40px) scale(0)",
      opacity: 0,
    },
  },
}));

export default function EditCampaign() {
  const { id } = useParams();
  const campaignFormikRef = useRef(null);
  const loopStepRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [videos, setVideos] = useState([]);
  const [toastStatus, setToastStatus] = useState(false);
  const [toastDescription, setToastDescription] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [alertModalText, setAlertModalText] = useState("");

  const [campaignData, setCampaignData] = useState(null);

  const navigate = useNavigate();

  const [loopsData, setLoopsData] = useState({
    selectedDevices: [],
    filters: {
      networks: [],
      partners: [],
      counties: [],
      cities: [],
      locations: [],
      tags: [],
      search: "",
    },
    timeInterval: 60,
  });

  const handleStatus = (description, setStatus, setDescription) => {
    setStatus(true);
    setDescription(description);
    setTimeout(() => {
      setStatus(false);
      setDescription("");
    }, 5000);
  };

  const handleTabChange = async (_, newTab) => {
    if (loadingSubmit) return;
    try {
      if (activeTab === 0 && newTab === 1) {
        // First validate form data before submitting
        const { name, client } = campaignFormikRef.current?.values || {};
        if (!name?.trim() || !client?.trim()) {
          handleStatus(
            "Numele campaniei si al clientului sunt obligatorii.",
            setToastStatus,
            setToastDescription
          );
          return;
        }

        if (!schedules || schedules.length === 0) {
          handleStatus(
            "Adauga cel putin o perioada cu video-uri inainte de a continua.",
            setToastStatus,
            setToastDescription
          );
          return;
        }

        // Check for duplicate periods (same start and end dates)
        const duplicatePeriods = [];
        for (let i = 0; i < schedules.length; i++) {
          for (let j = i + 1; j < schedules.length; j++) {
            if (
              schedules[i].startDate === schedules[j].startDate &&
              schedules[i].endDate === schedules[j].endDate
            ) {
              duplicatePeriods.push({
                period1: i + 1,
                period2: j + 1,
                startDate: schedules[i].startDate,
                endDate: schedules[i].endDate,
              });
            }
          }
        }

        if (duplicatePeriods.length > 0) {
          const duplicateMessages = duplicatePeriods.map(
            (dup) =>
              `Perioada ${dup.period1} si Perioada ${dup.period2} (${moment(
                dup.startDate
              ).format("DD.MM.YYYY")} - ${moment(dup.endDate).format(
                "DD.MM.YYYY"
              )})`
          );
          handleStatus(
            `Urmatoarele perioade au date identice si trebuie sa fie diferite: ${duplicateMessages.join(
              ", "
            )}`,
            setToastStatus,
            setToastDescription
          );
          return;
        }

        // Check for periods without videos
        const periodsWithoutVideos = schedules
          .map((schedule, index) => ({ schedule, index }))
          .filter(
            ({ schedule }) => !schedule.videos || schedule.videos.length === 0
          )
          .map(({ index }) => index + 1);

        if (periodsWithoutVideos.length > 0) {
          const periodText =
            periodsWithoutVideos.length === 1
              ? `Perioada ${periodsWithoutVideos[0]} nu are`
              : `Perioadele ${periodsWithoutVideos.join(", ")} nu au`;

          handleStatus(
            `${periodText} niciun spot adaugat. Adauga cel putin un spot pentru fiecare perioada.`,
            setToastStatus,
            setToastDescription
          );
          return;
        }

        // If all validations pass, then submit the form
        if (campaignFormikRef.current) {
          await campaignFormikRef.current.submitForm();
        }
      } else if (
        activeTab === 1 &&
        newTab === 0 &&
        loopStepRef.current?.saveLoopData
      ) {
        await loopStepRef.current.saveLoopData();
      }

      setActiveTab(newTab);
    } catch (error) {
      console.error("Error during tab switch:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const response = await GetCampaignInfo(id);
      if (response.status !== "OK") {
        throw new Error(response.error || "An error occurred");
      }

      // Check if this is a child campaign and redirect to parent
      if (response.redirect && response.parentId) {
        navigate("/campaigns");
        return;
      }

      const campaign = response.data;

      setCampaignData(campaign);

      // Format schedules for the new interface
      const formatToYYYYMMDD = (dateStr) => {
        if (!dateStr) return "";

        // Handle different date formats from backend
        if (typeof dateStr === "string") {
          // Try parsing different formats
          if (dateStr.includes("T")) {
            // ISO format: "2024-01-15T00:00:00Z" - convert to local date
            const date = new Date(dateStr);
            return isNaN(date.getTime())
              ? ""
              : date.toISOString().split("T")[0];
          } else if (
            dateStr.includes("-") &&
            dateStr.match(/^\d{4}-\d{2}-\d{2}$/)
          ) {
            // YYYY-MM-DD format - return as is to avoid timezone conversion issues
            return dateStr;
          } else {
            // Other formats - try to parse
            const date = new Date(dateStr);
            return isNaN(date.getTime())
              ? ""
              : date.toISOString().split("T")[0];
          }
        } else {
          // Non-string input
          const date = new Date(dateStr);
          return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
        }
      };

      // Use enhancedSchedules if available (with videos per schedule), otherwise fallback to old format
      let newSchedules = [];
      let newVideos = [];

      if (response.enhancedSchedules && response.enhancedSchedules.length > 0) {
        // Debug logging
        console.log("=== DEBUG Frontend - Enhanced schedules received ===");
        console.log(
          "Number of enhanced schedules:",
          response.enhancedSchedules.length
        );
        response.enhancedSchedules.forEach((enhSch, i) => {
          console.log(
            `Schedule ${i + 1}: ID=${enhSch.ID}, ${enhSch.start_date} to ${
              enhSch.end_date
            } with ${enhSch.videos?.length || 0} videos:`
          );
          if (enhSch.videos) {
            enhSch.videos.forEach((video, j) => {
              console.log(
                `  Video ${j + 1}: ID=${video.video_id}, Title=${
                  video.Video?.title
                }, Frequency=${video.frequency}`
              );
            });
          }
        });
        console.log("=== END DEBUG ===");

        // New format: schedules with their specific videos
        newSchedules = response.enhancedSchedules.map((enhSch) => ({
          id: enhSch.ID,
          originalId: enhSch.ID,
          startDate: formatToYYYYMMDD(enhSch.start_date),
          endDate: formatToYYYYMMDD(enhSch.end_date),
          videos:
            enhSch.videos?.map((vc) => ({
              id: vc.video_id,
              frequency: vc.frequency,
              title: vc.Video?.title || "",
            })) || [],
        }));

        // For backward compatibility, still set combined videos
        if (campaign.videos && campaign.videos.length > 0) {
          newVideos = campaign.videos.map((vc) => ({
            id: vc.video_id,
            frequency: vc.frequency,
            title: vc.Video?.title || "",
          }));
        }
      } else {
        // Fallback to old format (backward compatibility)
        newSchedules = campaign.schedules?.map((sch) => ({
          id: sch.ID,
          originalId: sch.ID,
          startDate: formatToYYYYMMDD(sch.start_date),
          endDate: formatToYYYYMMDD(sch.end_date),
          videos: [], // Empty videos array for old format
        })) || [
          {
            id: uuidv4(),
            startDate: new Date().toISOString().split("T")[0],
            endDate: new Date().toISOString().split("T")[0],
            videos: [],
          },
        ];

        // Format videos for the old interface (all videos combined)
        if (campaign.videos && campaign.videos.length > 0) {
          newVideos = campaign.videos.map((vc) => ({
            id: vc.video_id,
            frequency: vc.frequency,
            title: vc.Video?.title || "",
          }));
        }
      }

      setSchedules(newSchedules);
      setVideos(newVideos);

      const campaignDevices =
        campaign.devices && campaign.devices.length > 0
          ? campaign.devices.map((dev) => ({
              device_id: dev.device_id,
              name: dev.name,
            }))
          : [];

      if (campaign.loop) {
        const loop = campaign.loop;

        const loopFilters = {
          partners: loop.partners ?? [],
          counties: loop.counties ?? [],
          cities: loop.cities ?? [],
          locations: loop.locations ?? [],
          tags: loop.tags ?? [],
          search: "",
        };

        const loopTimeInterval = loop.target_duration
          ? Math.round(loop.target_duration / 60)
          : 1;

        setLoopsData({
          selectedDevices: campaignDevices,
          filters: loopFilters,
          timeInterval: loopTimeInterval,
        });
      } else {
        setLoopsData({
          selectedDevices: [],
          filters: {
            partners: [],
            counties: [],
            cities: [],
            locations: [],
            tags: [],
            search: "",
          },
          timeInterval: 60,
        });
      }
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAll = async (updatedLoopsData, isDraft = false) => {
    if (loadingSubmit) return;
    setLoadingSubmit(true);

    let shouldNavigate = true;

    try {
      // Use campaignData.schedules if available, otherwise fall back to schedules state
      const campaignSchedules = campaignData?.schedules || schedules || [];

      // Process schedules with their individual videos (including new video uploads)
      const processedSchedules = [];

      for (const schedule of campaignSchedules) {
        const scheduleVideos = [];

        // Handle existing videos for this schedule
        if (schedule.videos) {
          for (const video of schedule.videos) {
            // Only handle existing videos (no new video uploads)
            if (video.id) {
              scheduleVideos.push({
                id: video.id,
                frequency: video.frequency || 1,
              });
            }
          }
        }

        processedSchedules.push({
          id: schedule.id,
          startDate: schedule.startDate,
          endDate: schedule.endDate,
          videos: scheduleVideos,
        });
      }

      // Create payload for EditCampaign backend according to the expected structure
      const finalPayload = {
        name: campaignData?.name,
        client: campaignData?.client,
        observations: campaignData?.observations,
        is_draft: isDraft,
        schedules: processedSchedules,
        loop: {
          filters: {
            partners: updatedLoopsData.filters.partners || [],
            counties: updatedLoopsData.filters.counties || [],
            cities: updatedLoopsData.filters.cities || [],
            locations: updatedLoopsData.filters.locations || [],
            tags: updatedLoopsData.filters.tags || [],
          },
          timeInterval: updatedLoopsData.timeInterval,
        },
        devices: updatedLoopsData.selectedDevices.map((device) => ({
          device_id: device.device_id,
        })),
      };

      const response = await UpdateCampaignDetails(
        id,
        JSON.stringify(finalPayload)
      );

      if (response.status !== "OK") {
        // Check if this is a child campaign redirect
        if (response.parentId) {
          navigate(`/campaigns/${response.parentId}`, { replace: true });
          return;
        }
        throw new Error(response.error || response.message || "Update failed");
      }

      if (response.status === "OK") {
        if (response.regenerationTriggered) {
          setAlertModalText(
            "Campania a fost actualizata cu succes. Regenerarea playlist-ului a fost initiata."
          );
        } else {
          setAlertModalText(
            "Campania a fost actualizata cu succes. Playlist-ul ramane neschimbat deoarece regenerarea nu a fost necesara."
          );
        }

        setOpenModal(true);
        shouldNavigate = false;

        setTimeout(() => {
          setOpenModal(false);
          navigate("/campaigns");
        }, 2500);

        return;
      }
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoadingSubmit(false);
      if (shouldNavigate) {
        navigate(`/campaigns`, {
          state: {
            campaignId: id,
          },
        });
      }
    }
  };

  const handleCampaignChange = useCallback((updatedData) => {
    setCampaignData((prev) => ({
      ...prev,
      ...updatedData,
    }));

    // Also update schedules if they changed
    if (updatedData.schedules) {
      setSchedules(updatedData.schedules);
    }
  }, []);

  const handleLoopsChange = useCallback((updatedLoopsData) => {
    setLoopsData((prev) => ({
      ...prev,
      ...updatedLoopsData,
    }));
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {toastStatus && <PopSnackbar message={toastDescription} />}
      {loading ? (
        <CircularProgress />
      ) : (
        <div>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <StyledTab
              icon={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <StepIndicator
                    active={activeTab === 0}
                    completed={activeTab > 0}
                  >
                    {activeTab > 0 ? <Check fontSize="small" /> : "1"}
                  </StepIndicator>
                  <CampaignIcon />
                </div>
              }
              label="Configureaza campania"
              iconPosition="start"
            />
            <StyledTab
              icon={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <StepIndicator
                    active={activeTab === 1}
                    completed={activeTab > 1}
                  >
                    2
                  </StepIndicator>
                  <LoopIcon />
                </div>
              }
              label="Selectie playere"
              iconPosition="start"
            />
            <ProgressArrow activeTab={activeTab} />
          </StyledTabs>

          <TabPanel hidden={activeTab !== 0}>
            <EditCampaignStep
              setToastDescription={setToastDescription}
              setToastStatus={setToastStatus}
              handleStatus={handleStatus}
              goNext={() => setActiveTab(1)}
              onChange={handleCampaignChange}
              data={campaignData}
              videos={videos}
              formikRef={campaignFormikRef}
              initialCalendars={schedules}
            />
          </TabPanel>

          <TabPanel hidden={activeTab !== 1}>
            <EditLoopStep
              ref={loopStepRef}
              data={loopsData}
              onChange={handleLoopsChange}
              goBack={() => setActiveTab(0)}
              campaignData={campaignData}
              handleStatus={handleStatus}
              onSubmitAll={handleSubmitAll}
              loadingbtn={loadingSubmit}
            />
          </TabPanel>
        </div>
      )}
      <AlertModal
        open={openModal}
        text={alertModalText}
        handleClose={() => {
          setOpenModal(false);
          navigate(`/campaigns`, {
            state: {
              campaignId: id,
            },
          });
        }}
        showYesButton={false}
        showNoButton={false}
        showOkButton={true}
        loading={false}
      />
    </>
  );
}
